import React, { useState, useEffect, useMemo } from 'react';
import {
	BriefcaseIcon,
	CalendarIcon,
	ClockIcon,
	PhoneIcon,
	CheckCircleIcon,
	XCircleIcon,
	ExclamationCircleIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	ChevronLeftIcon,
	ChevronRightIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getPositionInterviewSlots, updateCandidateStatus, updatePositionInterviewSlotStatus } from '../../services/aiRecruiterService';
import { useBrandingColors } from '../../hooks/useBrandingColors';
import { useVacancyStore } from '../../store/vacancyStore';
import type { CandidateStatus, InterviewSlot, InterviewStatus } from '../../types/aiRecruiter';
import InterviewDetailsModal from './InterviewDetailsModal';
import DateRangePicker from './DateRangePicker';
import CalendarLineIcon from '../../assets/icons/calendar-line.svg';

type InterviewDayGroup = {
	date: string;
	items: InterviewSlot[];
};

type StatusFilter = 'all' | 'BOOKED' | 'CONFIRMED' | 'COMPLETED' | 'APPROVED' | 'REJECTED';

const CANDIDATE_DECISION_BY_INTERVIEW_STATUS: Partial<Record<InterviewStatus, CandidateStatus>> = {
	APPROVED: 'QUALIFIED',
	REJECTED: 'REJECTED'
};

const InterviewsTab: React.FC = () => {
	const { selectedVacancyId } = useVacancyStore();
	const [interviews, setInterviews] = useState<InterviewSlot[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingInterviews, setLoadingInterviews] = useState(false);
	const [collapsedDates, setCollapsedDates] = useState<Record<string, boolean>>({});
    const colors = useBrandingColors();

	const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
	const [dateRangeStart, setDateRangeStart] = useState<Date | null>(null);
	const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(null);
	const [isCalendarOpen, setIsCalendarOpen] = useState(false);

	const [selectedInterview, setSelectedInterview] = useState<InterviewSlot | null>(null);

	useEffect(() => {
		if (interviews.length > 0) {
			const dates = interviews.map(i => new Date(i.slotDate).getTime());
			const minDate = new Date(Math.min(...dates));
			const maxDate = new Date(Math.max(...dates));
			const startOfWeek = new Date(minDate);
			startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + 1);
			const endOfWeek = new Date(startOfWeek);
			endOfWeek.setDate(endOfWeek.getDate() + 6);
			setDateRangeStart(startOfWeek);
			setDateRangeEnd(endOfWeek);
		}
	}, [interviews]);

	const shiftWeek = (direction: 'prev' | 'next') => {
		if (!dateRangeStart || !dateRangeEnd) return;
		const days = direction === 'prev' ? -7 : 7;
		const newStart = new Date(dateRangeStart);
		newStart.setDate(newStart.getDate() + days);
		const newEnd = new Date(dateRangeEnd);
		newEnd.setDate(newEnd.getDate() + days);
		setDateRangeStart(newStart);
		setDateRangeEnd(newEnd);
	};

	const formatDateRange = (): string => {
		if (!dateRangeStart || !dateRangeEnd) return '';
		const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
		const startStr = dateRangeStart.toLocaleDateString('ru-RU', opts);
		const endStr = dateRangeEnd.toLocaleDateString('ru-RU', opts);
		return `${startStr} - ${endStr}`;
	};

	const handleDateRangeSelect = (range: { from: Date | null; to: Date | null }) => {
		setDateRangeStart(range.from);
		setDateRangeEnd(range.to);
	};

	useEffect(() => {
		if (!selectedVacancyId) {
			return;
		}

		loadInterviewSlots();
	}, [selectedVacancyId]);

	const loadInterviewSlots = async () => {
		if (!selectedVacancyId) return;

		setLoading(true);
		setLoadingInterviews(true);
		try {
			const slots = await getPositionInterviewSlots(selectedVacancyId);
			setInterviews(slots);
		} catch (error) {
			console.error('Failed to load interview slots:', error);
			setInterviews([]);
		} finally {
			setLoading(false);
			setLoadingInterviews(false);
		}
	};

	const showNotificationResult = (result: string | undefined) => {
		switch (result) {
			case 'SENT':
				toast.success('Уведомление отправлено кандидату');
				break;
			case 'NO_DIALOGUE':
				toast('Диалог не найден, уведомление не отправлено', { icon: '⚠️' });
				break;
			case 'NO_CHANNEL':
				toast('Канал не найден, уведомление не отправлено', { icon: '⚠️' });
				break;
		}
	};

	const updateStatus = async (interview: InterviewSlot, newStatus: InterviewStatus) => {
		if (!selectedVacancyId) return;

		try {
			const candidateDecisionStatus = CANDIDATE_DECISION_BY_INTERVIEW_STATUS[newStatus];
			if (candidateDecisionStatus && interview.campaignId && interview.candidateId) {
				await updateCandidateStatus(
					interview.campaignId,
					interview.candidateId,
					candidateDecisionStatus,
					{ sendDecisionNotification: true }
				);
			}

			const result = await updatePositionInterviewSlotStatus(selectedVacancyId, interview.id, { status: newStatus });
			showNotificationResult(result?.notificationResult);
			loadInterviewSlots();
		} catch (error) {
			console.error('Failed to update interview status:', error);
			alert('Не удалось обновить статус собеседования');
		}
	};

	const getStatusIcon = (status: InterviewStatus) => {
		switch (status) {
			case 'BOOKED':
				return <ClockIcon className="w-5 h-5 text-yellow-500" />;
			case 'CONFIRMED':
				return <CheckCircleIcon className="w-5 h-5 text-blue-500" />;
			case 'COMPLETED':
				return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
			case 'APPROVED':
				return <CheckCircleIcon className="w-5 h-5 text-[#007A55]" />;
			case 'CANCELLED':
				return <XCircleIcon className="w-5 h-5 text-blue-500" />;
			case 'REJECTED':
				return <XCircleIcon className="w-5 h-5 text-[#AF1D38]" />;
			case 'NO_SHOW':
				return <ExclamationCircleIcon className="w-5 h-5 text-orange-500" />;
			default:
				return <ClockIcon className="w-5 h-5 text-gray-500" />;
		}
	};

	const getStatusText = (status: InterviewStatus): string => {
		const statusMap: Record<InterviewStatus, string> = {
			BOOKED: 'Ожидает',
			CONFIRMED: 'Подтверждено',
			COMPLETED: 'Проведено',
			APPROVED: 'Одобрен',
			REJECTED: 'Отклонен',
			CANCELLED: 'Отменено',
			NO_SHOW: 'Не явился'
		};
		return statusMap[status] || status;
	};

	const getStatusColor = (status: InterviewStatus): string => {
		const colorMap: Record<InterviewStatus, string> = {
			BOOKED: 'bg-[#FFFBEB] text-[#BB4D00]',
			CONFIRMED: 'bg-[#DBEAFE] text-[#1E40AF]',
			COMPLETED: 'bg-[#ECFDF5] text-[#007A55]',
			APPROVED: 'bg-[#ECFDF5] text-[#007A55]',
			REJECTED: 'bg-[#FDEDF0] text-[#AF1D38]',
			CANCELLED: 'bg-[#DBEAFE] text-[#1447E6]',
			NO_SHOW: 'bg-[#FFEDD5] text-[#9A3412]'
		};
		return colorMap[status] || 'bg-gray-100 text-gray-800';
	};

	const formatTime = (timeStr: string): string => {
		return timeStr;
	};

	const getDayInfo = (dateStr: string) => {
		const date = new Date(dateStr);
		const weekday = date
			.toLocaleDateString('ru-RU', {
				weekday: 'long'
			})
			.toLowerCase();
		const fullDate = date.toLocaleDateString('ru-RU', {
			day: 'numeric',
			month: 'long',
			year: 'numeric'
		});
		return { weekday, fullDate };
	};

	const getInterviewsCountLabel = (count: number): string => {
		const mod10 = count % 10;
		const mod100 = count % 100;

		if (mod10 === 1 && mod100 !== 11) {
			return `${count} собеседование`;
		}
		if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
			return `${count} собеседования`;
		}
		return `${count} собеседований`;
	};

	const groupInterviewsByDate = (list: InterviewSlot[]): InterviewDayGroup[] => {
		const map = new Map<string, InterviewSlot[]>();

		for (const interview of list) {
			if (!map.has(interview.slotDate)) {
				map.set(interview.slotDate, []);
			}
			map.get(interview.slotDate)!.push(interview);
		}

		const groups: InterviewDayGroup[] = Array.from(map.entries()).map(([date, items]) => {
			const itemsSorted = [...items].sort((a, b) => {
				const timeA = new Date(a.slotDate + 'T' + a.slotTime);
				const timeB = new Date(b.slotDate + 'T' + b.slotTime);
				return timeA.getTime() - timeB.getTime();
			});

			return {
				date,
				items: itemsSorted
			};
		});

		const todayStr = new Date().toISOString().split('T')[0];

		groups.sort((a, b) => {
			const aIsToday = a.date === todayStr;
			const bIsToday = b.date === todayStr;

			if (aIsToday && !bIsToday) return -1;
			if (!aIsToday && bIsToday) return 1;

			const dateA = new Date(a.date).getTime();
			const dateB = new Date(b.date).getTime();
			return dateB - dateA;
		});

		return groups;
	};

	const toggleDate = (date: string) => {
		setCollapsedDates(prev => ({
			...prev,
			[date]: !prev[date]
		}));
	};

	const isUpcoming = (date: string, time: string): boolean => {
		const interviewDateTime = new Date(date + 'T' + time);
		return interviewDateTime > new Date();
	};

	const filteredInterviews = useMemo(() => {
		let result = interviews;

		if (statusFilter !== 'all') {
			result = result.filter(i => i.status === statusFilter);
		}

		if (dateRangeStart && dateRangeEnd) {
			const startDate = new Date(dateRangeStart);
			startDate.setHours(0, 0, 0, 0);
			const endDate = new Date(dateRangeEnd);
			endDate.setHours(23, 59, 59, 999);

			result = result.filter(i => {
				const d = new Date(i.slotDate);
				d.setHours(12, 0, 0, 0);
				return d >= startDate && d <= endDate;
			});
		}

		return result;
	}, [interviews, statusFilter, dateRangeStart, dateRangeEnd]);

	const groupedInterviews = groupInterviewsByDate(filteredInterviews);

	useEffect(() => {
		if (groupedInterviews.length > 0) {
			const collapsed: Record<string, boolean> = {};
			groupedInterviews.forEach((group, idx) => {
				collapsed[group.date] = idx !== 0;
			});
			setCollapsedDates(collapsed);
		}
	}, [filteredInterviews]);

	const statusFilters: { key: StatusFilter; label: string }[] = [
		{ key: 'all', label: 'Все' },
		{ key: 'BOOKED', label: 'Ожидают' },
		{ key: 'CONFIRMED', label: 'Подтверждено' },
		{ key: 'APPROVED', label: 'Одобрены' },
		{ key: 'REJECTED', label: 'Отклонены' },
		{ key: 'COMPLETED', label: 'Завершено' }
	];

	const closeModal = () => setSelectedInterview(null);

	const handleConfirmSlot = async () => {
		if (!selectedInterview) return;
		await updateStatus(selectedInterview, 'CONFIRMED');
		setSelectedInterview(prev => prev ? { ...prev, status: 'CONFIRMED' } : null);
	};

	const handleRejectSlot = async () => {
		if (!selectedInterview) return;
		await updateStatus(selectedInterview, 'CANCELLED');
		closeModal();
	};

	const handleCompleteSlot = async () => {
		if (!selectedInterview) return;
		await updateStatus(selectedInterview, 'COMPLETED');
		setSelectedInterview(prev => prev ? { ...prev, status: 'COMPLETED' } : null);
	};

	const handleApproveCandidate = async () => {
		if (!selectedInterview) return;
		await updateStatus(selectedInterview, 'APPROVED');
		setSelectedInterview(prev => prev ? { ...prev, status: 'APPROVED' } : null);
	};

	const handleRejectCandidate = async () => {
		if (!selectedInterview) return;
		await updateStatus(selectedInterview, 'REJECTED');
		closeModal();
	};

	const handleCancelInterview = async () => {
		if (!selectedInterview) return;
		await updateStatus(selectedInterview, 'CANCELLED');
		closeModal();
	};

	return (
		<div className="bg-[#f5f6f1]">
			<div className="bg-[#f5f6f1] px-5 pt-6 pb-2">
				<h2 className="text-2xl ml-2 font-semibold text-[#0a0d14]">Собеседования</h2>
			</div>

			<div className="p-6">
				{interviews.length > 0 && (
					<div className="flex items-center justify-between p-1 bg-white border border-[#E2E8F0] rounded-[12px] mb-4">
						<div className="flex items-center gap-2 h-9 relative">
							<button
								type="button"
								onClick={() => shiftWeek('prev')}
								className="w-9 h-9 flex items-center justify-center rounded-[8px] hover:bg-[#F1F5F9] transition-colors"
							>
								<ChevronLeftIcon className="w-4 h-4 text-[#62748E]" />
							</button>
							<button
								type="button"
								onClick={() => setIsCalendarOpen(!isCalendarOpen)}
								className="px-2 h-9 flex items-center gap-2 justify-center rounded-[8px] hover:bg-[#F1F5F9] transition-colors cursor-pointer"
							>
								<span className="text-[16px] font-semibold text-[#314158] capitalize">
									{formatDateRange()}
								</span>
								<img src={CalendarLineIcon} alt="" className="w-5 h-5" />
							</button>
							<button
								type="button"
								onClick={() => shiftWeek('next')}
								className="w-9 h-9 flex items-center justify-center rounded-[8px] hover:bg-[#F1F5F9] transition-colors"
							>
								<ChevronRightIcon className="w-4 h-4 text-[#62748E]" />
							</button>

							<DateRangePicker
								isOpen={isCalendarOpen}
								onClose={() => setIsCalendarOpen(false)}
								selectedRange={{ from: dateRangeStart, to: dateRangeEnd }}
								onSelect={handleDateRangeSelect}
								primaryColor={colors.primary}
							/>
						</div>

						<div className="flex items-center h-9 bg-[#F1F5F9] rounded-[8px] p-[2px]">
							{statusFilters.map(sf => (
								<button
									key={sf.key}
									type="button"
									onClick={() => setStatusFilter(sf.key)}
									className={`h-full px-2 py-1 rounded-[6px] text-[12px] font-medium transition-colors ${
										statusFilter === sf.key
											? 'bg-white text-[#0A0A0A] shadow-sm'
											: 'text-[#0A0A0A] hover:bg-white/50'
									}`}
								>
									{sf.label}
								</button>
							))}
						</div>
					</div>
				)}

				{loadingInterviews ? (
					<div className="flex items-center justify-center py-12">
						<div className="text-gray-500">Загрузка собеседований...</div>
					</div>
				) : groupedInterviews.length === 0 ? (
					<div className="flex items-center justify-center py-12">
						<div className="text-gray-500">Нет собеседований за выбранный период</div>
					</div>
				) : (
					<div className="flex flex-col gap-4">
						{groupedInterviews.map(group => {
							const { weekday, fullDate } = getDayInfo(group.date);
							const isCollapsed = collapsedDates[group.date];
							const countLabel = getInterviewsCountLabel(group.items.length);
							const isToday =
								new Date(group.date).toDateString() === new Date().toDateString();

							return (
								<div key={group.date} className="bg-white rounded-[16px] overflow-hidden">
									<div className="flex items-center justify-between gap-4 px-4 py-3">
										<div className="flex items-center gap-3">
											<button
												type="button"
												onClick={() => toggleDate(group.date)}
												className="w-6 h-6 flex items-center justify-center text-[#90A1B9] hover:text-[#62748E] transition-colors shrink-0"
											>
												{isCollapsed ? (
													<ChevronDownIcon className="w-5 h-5" />
												) : (
													<ChevronUpIcon className="w-5 h-5" />
												)}
											</button>
											<div className="flex flex-col">
												<div
													className={`text-[20px] leading-[32px] font-bold capitalize ${
														isToday ? 'text-[#E16349]' : 'text-[#0F172B]'
													}`}
												>
													{weekday}
												</div>
												<div className="text-[14px] leading-[20px] font-medium text-[#62748E]">
													{fullDate}
												</div>
											</div>
												</div>
										<div className="flex items-center gap-3">
											<div className="text-[14px] leading-[20px] font-medium text-[#90A1B9] whitespace-nowrap">
												{countLabel}
											</div>
										</div>
									</div>

									{!isCollapsed && (
										<div className="px-4 pb-4 pt-0 flex flex-wrap gap-4">
											{group.items.map(interview => {
												const isPast = !isUpcoming(interview.slotDate, interview.slotTime);
												const needsAttention =
													isPast && interview.status !== 'COMPLETED' && interview.status !== 'CANCELLED';
												const canChangeStatus =
													interview.status === 'BOOKED' || interview.status === 'CONFIRMED';

												return (
													<div
														key={interview.id}
														onClick={() => setSelectedInterview(interview)}
														className={`bg-white border rounded-[14px] p-4 flex flex-col justify-between box-border transition-all duration-300 cursor-pointer hover:shadow-md ${
															canChangeStatus ? 'border-[#CDD0D5]' : 'border-[#E5E7EB]'
														}`}
														style={{ width: '32%', minWidth: '320px', height: '159px' }}
													>
														<div className="flex items-center justify-between gap-3">
															<div className="flex items-center gap-1 text-[18px] leading-[28px]">
																<span className="font-semibold text-[#0F172B]">
																	{formatTime(interview.slotTime)}
																</span>
																<span className="text-[#CAD5E2]">–</span>
																<span className="text-[16px] font-medium text-[#62748E]">
																	{(() => {
																		const parts = (interview.slotTime || '').split(':');
																		const hours = Number(parts[0]);
																		const minutes = Number(parts[1]);
																		if (Number.isNaN(hours) || Number.isNaN(minutes)) {
																			return formatTime(interview.slotTime);
																		}
																		const endHours = (hours + 1) % 24;
																		return `${String(endHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
																	})()}
																</span>
						</div>

															<div className="flex gap-1 shrink-0">
																{canChangeStatus && interview.status === 'BOOKED' && (
																	<>
																		<div className="relative group/confirm flex items-center justify-center">
																			<button
																				onClick={(e) => { e.stopPropagation(); updateStatus(interview, 'CONFIRMED'); }}
																				className="w-8 h-8 rounded-[8px] bg-[#EFFAF6] text-[#2D9F75] flex items-center justify-center hover:opacity-80 transition-opacity"
																			>
																				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8.5 12.379L15.394 5.484L16.455 6.545L8.5 14.5L3.727 9.727L4.787 8.666L8.5 12.379Z" fill="currentColor"/></svg>
																			</button>
																			<div className="absolute bottom-full mb-2 hidden group-hover/confirm:flex flex-col items-center z-50 pointer-events-none">
																				<div
																					className="bg-[#20232D] text-white text-[12px] leading-[16px] py-[2px] px-[6px] rounded-[4px] whitespace-nowrap"
																					style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24), 0px 12px 24px 0px rgba(134, 140, 152, 0.12)' }}
																				>
																					Подтвердить слот
																				</div>
																				<div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#20232D]" />
																			</div>
																		</div>
																		<div className="relative group/cancel flex items-center justify-center">
																			<button
																				onClick={(e) => { e.stopPropagation(); updateStatus(interview, 'CANCELLED'); }}
																				className="w-8 h-8 rounded-[8px] bg-[#FDEDF0] text-[#AF1D38] flex items-center justify-center hover:opacity-80 transition-opacity"
																			>
																				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 8.94L13.712 5.227L14.773 6.288L11.06 10L14.773 13.713L13.712 14.773L10 11.06L6.287 14.773L5.227 13.713L8.939 10L5.227 6.288L6.287 5.227L10 8.94Z" fill="currentColor"/></svg>
																			</button>
																			<div className="absolute bottom-full mb-2 hidden group-hover/cancel:flex flex-col items-center z-50 pointer-events-none">
																				<div
																					className="bg-[#20232D] text-white text-[12px] leading-[16px] py-[2px] px-[6px] rounded-[4px] whitespace-nowrap"
																					style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24), 0px 12px 24px 0px rgba(134, 140, 152, 0.12)' }}
																				>
																					Отклонить слот
																				</div>
																				<div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#20232D]" />
																			</div>
																		</div>
																	</>
																)}
																{canChangeStatus && interview.status === 'CONFIRMED' && (
																	<>
																		<div className="relative group/approve flex items-center justify-center">
																			<button
																				onClick={(e) => { e.stopPropagation(); updateStatus(interview, 'APPROVED'); }}
																				className="w-8 h-8 rounded-[8px] bg-[#EFFAF6] text-[#2D9F75] flex items-center justify-center hover:opacity-80 transition-opacity"
																			>
																				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.334 17.5V15.833C13.334 14.949 12.983 14.101 12.357 13.476C11.732 12.851 10.884 12.5 10 12.5H5C4.116 12.5 3.268 12.851 2.643 13.476C2.018 14.101 1.667 14.949 1.667 15.833V17.5" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/><path d="M7.5 9.167C9.341 9.167 10.834 7.674 10.834 5.833C10.834 3.992 9.341 2.5 7.5 2.5C5.659 2.5 4.167 3.992 4.167 5.833C4.167 7.674 5.659 9.167 7.5 9.167Z" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.333 9.167L15 10.833L18.333 7.5" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/></svg>
																			</button>
																			<div className="absolute bottom-full mb-2 hidden group-hover/approve:flex flex-col items-center z-50 pointer-events-none">
																				<div
																					className="bg-[#20232D] text-white text-[12px] leading-[16px] py-[2px] px-[6px] rounded-[4px] whitespace-nowrap"
																					style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24), 0px 12px 24px 0px rgba(134, 140, 152, 0.12)' }}
																				>
																					Кандидат одобрен
																				</div>
																				<div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#20232D]" />
																			</div>
																		</div>
																		<div className="relative group/reject flex items-center justify-center">
																			<button
																				onClick={(e) => { e.stopPropagation(); updateStatus(interview, 'REJECTED'); }}
																				className="w-8 h-8 rounded-[8px] bg-[#FDEDF0] text-[#AF1D38] flex items-center justify-center hover:opacity-80 transition-opacity"
																			>
																				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.334 17.5V15.833C13.334 14.949 12.983 14.101 12.357 13.476C11.732 12.851 10.884 12.5 10 12.5H5C4.116 12.5 3.268 12.851 2.643 13.476C2.018 14.101 1.667 14.949 1.667 15.833V17.5" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/><path d="M7.5 9.167C9.341 9.167 10.834 7.674 10.834 5.833C10.834 3.992 9.341 2.5 7.5 2.5C5.659 2.5 4.167 3.992 4.167 5.833C4.167 7.674 5.659 9.167 7.5 9.167Z" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/><path d="M14.167 6.667L18.334 10.833" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/><path d="M18.334 6.667L14.167 10.833" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/></svg>
																			</button>
																			<div className="absolute bottom-full mb-2 hidden group-hover/reject:flex flex-col items-center z-50 pointer-events-none">
																				<div
																					className="bg-[#20232D] text-white text-[12px] leading-[16px] py-[2px] px-[6px] rounded-[4px] whitespace-nowrap"
																					style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24), 0px 12px 24px 0px rgba(134, 140, 152, 0.12)' }}
																				>
																					Кандидат отклонен
																				</div>
																				<div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#20232D]" />
																			</div>
																		</div>
																		<div className="relative group/cancelint flex items-center justify-center">
																			<button
																				onClick={(e) => { e.stopPropagation(); updateStatus(interview, 'CANCELLED'); }}
																				className="w-8 h-8 rounded-[8px] bg-[#EFF6FF] text-[#1447E6] flex items-center justify-center hover:opacity-80 transition-opacity"
																			>
																				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 18.333C14.603 18.333 18.334 14.602 18.334 10C18.334 5.398 14.603 1.667 10 1.667C5.398 1.667 1.667 5.398 1.667 10C1.667 14.602 5.398 18.333 10 18.333Z" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/><path d="M4.083 4.083L15.917 15.917" stroke="currentColor" strokeWidth="1.333" strokeLinecap="round" strokeLinejoin="round"/></svg>
																			</button>
																			<div className="absolute bottom-full mb-2 hidden group-hover/cancelint:flex flex-col items-center z-50 pointer-events-none">
																				<div
																					className="bg-[#20232D] text-white text-[12px] leading-[16px] py-[2px] px-[6px] rounded-[4px] whitespace-nowrap"
																					style={{ boxShadow: '0px 1px 2px 0px rgba(228, 229, 231, 0.24), 0px 12px 24px 0px rgba(134, 140, 152, 0.12)' }}
																				>
																					Интервью отменено
																				</div>
																				<div className="w-0 h-0 border-l-[4px] border-l-transparent border-r-[4px] border-r-transparent border-t-[4px] border-t-[#20232D]" />
																			</div>
																		</div>
																	</>
																)}
															</div>
														</div>

														<div className="flex items-center gap-3">
															<div className="w-10 h-10 rounded-full bg-[#F1F5F9] flex items-center justify-center text-[#45556C] text-[12px] font-bold shrink-0">
																{interview.candidateName
																	? interview.candidateName
																			.split(' ')
																			.filter(Boolean)
																			.map(n => n[0])
																			.join('')
																			.slice(0, 2)
																			.toUpperCase()
																	: '??'}
															</div>
															<div className="flex flex-col gap-1">
																<div className="text-[14px] leading-[18px] font-medium text-[#0F172B] truncate max-w-[280px]">
																	{interview.candidateName || 'Не указано'}
												</div>
																{interview.campaignName && (
																	<div className="text-[12px] leading-[16px] text-[#62748E] truncate max-w-[280px]">
																		{interview.campaignName}
											</div>
										)}
												</div>
											</div>

														<div className="h-px bg-[#F8FAFC]" />

														<div className="flex items-center justify-between gap-3">
															<div className="flex items-center gap-2 text-[12px] leading-[16px] text-[#62748E]">
																<PhoneIcon className="w-4 h-4 text-[#62748E]" />
																<span>{interview.candidatePhone || '—'}</span>
												</div>
															<div className="flex flex-col items-end gap-1">
																<span
																	className={`px-2 py-[2px] rounded-full text-[10px] font-semibold uppercase tracking-[0.06em] ${getStatusColor(
																		interview.status
																	)}`}
																>
																	{getStatusText(interview.status)}
																</span>
																{needsAttention && (
																	<div className="text-[11px] leading-[16px] text-orange-600 font-medium" />
																)}
															</div>
												</div>
													</div>
												);
											})}
											</div>
										)}
								</div>
							);
						})}
					</div>
				)}
			</div>

			{selectedInterview && (
				<InterviewDetailsModal
					interview={selectedInterview}
					onClose={closeModal}
					onConfirm={handleConfirmSlot}
					onReject={handleRejectSlot}
					onComplete={handleCompleteSlot}
					onApprove={handleApproveCandidate}
					onRejectCandidate={handleRejectCandidate}
					onCancelInterview={handleCancelInterview}
				/>
			)}
		</div>
	);
};

export default InterviewsTab;
